/**
 * PATH: packages/ui/src/kanban/meetingRecorderEngine.ts
 * FILE_ID: UI-KANBAN-MEETING-RECORDER-ENGINE-001
 * PURPOSE: Framework-free chunked mic capture engine for meeting mode. Keeps
 *   ONE getUserMedia stream open and cycles a fresh MediaRecorder every
 *   `chunkMs`, so each emitted blob is a COMPLETE audio container the server
 *   can transcribe (webm/opus timeslice chunks are not independently
 *   decodable, which is why we restart rather than slice).
 * WHY: BUG-2026-07-02-MEETING-RECORDING-DIES-ON-NAV — the recorder logic used
 *   to live inside the React hook `useMeetingRecorder`, whose lifetime was
 *   tied to MeetingModePanel's mount. Navigating boards/routes unmounted the
 *   panel and killed the mic mid-meeting. The capture loop is now a plain
 *   object owned by the module-level meetingSession service, so it survives
 *   any React unmount; only an explicit stop() (or fatal error) ends it.
 *   Logic is a verbatim port of the former hook body (instant-text ramp
 *   included: the FIRST chunk is intentionally SHORT — `firstChunkMs` — so
 *   the user sees transcribed text within seconds of pressing record).
 * FLOW:
 *   start() → getUserMedia (once) → loop[ MediaRecorder.start →
 *             wait firstChunkMs (chunk 0) then chunkMs → stop →
 *             transcribe(blob) → onSegment(text) ]
 *   stop()  → end loop → flush + transcribe final chunk → release mic
 * DEPENDENCIES:
 *   - KanbanApi.transcribeAudio (injected as `transcribe`)
 * RELATED:
 *   - meetingSession.ts: the sole production owner of an engine instance.
 */

export interface MeetingRecorderEngineArgs {
  transcribe: (
    blob: Blob,
    opts?: { language?: string; filename?: string },
  ) => Promise<string>
  /** Called with each transcribed chunk's text (non-empty, trimmed). */
  onSegment: (text: string) => void
  /** Optional — archive each raw audio chunk for post-meeting diarization. */
  onAudioChunk?: (blob: Blob, seq: number, filename: string) => void
  onError?: (code: string) => void
  /** Mirrors the recording flag (true after start succeeds, false on stop). */
  onRecordingChange?: (recording: boolean) => void
  /** Mirrors "≥1 transcription round-trip in flight". */
  onTranscribingChange?: (transcribing: boolean) => void
  language?: string
  /** Steady chunk length in ms (from the 2nd chunk on). ~8s balances a "live"
   * update cadence against per-request overhead + per-chunk accuracy. */
  chunkMs?: number
  /** First chunk length in ms. Kept SHORT so the very first transcribed text
   * appears within a few seconds of pressing record (instant feedback). */
  firstChunkMs?: number
}

export interface MeetingRecorderEngine {
  isRecording: () => boolean
  isTranscribing: () => boolean
  start: () => Promise<void>
  stop: () => void
}

export function isRecorderSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined' &&
    !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
  )
}

function pickMimeType(): string {
  const candidates = ['audio/webm', 'audio/mp4', 'audio/ogg']
  const MR = typeof window !== 'undefined' ? window.MediaRecorder : undefined
  if (MR && typeof MR.isTypeSupported === 'function') {
    for (const t of candidates) {
      try { if (MR.isTypeSupported(t)) return t } catch { /* ignore */ }
    }
  }
  return ''
}

function extForMime(mime: string): string {
  if (mime.includes('mp4')) return 'mp4'
  if (mime.includes('ogg')) return 'ogg'
  return 'webm'
}

export function createMeetingRecorder(
  args: MeetingRecorderEngineArgs,
): MeetingRecorderEngine {
  const {
    transcribe,
    onSegment,
    onAudioChunk,
    onError,
    onRecordingChange,
    onTranscribingChange,
    language,
    chunkMs = 8_000,
    firstChunkMs = 4_000,
  } = args

  let stream: MediaStream | null = null
  let rec: MediaRecorder | null = null
  let running = false
  let recording = false
  let timer: ReturnType<typeof setTimeout> | null = null
  let mime = ''
  // Count of in-flight transcription round-trips; isTranscribing mirrors >0.
  let inFlight = 0
  // 0-based index of the chunk being captured; chunk 0 uses the short
  // `firstChunkMs` window for instant first text, the rest use steady `chunkMs`.
  let chunkIndex = 0
  let audioSeq = 0

  const setRecording = (next: boolean) => {
    if (recording === next) return
    recording = next
    onRecordingChange?.(next)
  }

  const setInFlight = (delta: number) => {
    const was = inFlight > 0
    inFlight = Math.max(0, inFlight + delta)
    const now = inFlight > 0
    if (was !== now) onTranscribingChange?.(now)
  }

  const teardownStream = () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    if (stream) {
      for (const track of stream.getTracks()) {
        try { track.stop() } catch { /* ignore */ }
      }
      stream = null
    }
    rec = null
  }

  const startChunk = () => {
    if (!stream || !running) return
    // Short window for the very first chunk (instant text), steady thereafter.
    const chunkIdx = chunkIndex
    chunkIndex = chunkIdx + 1
    const thisChunkMs = chunkIdx === 0 ? firstChunkMs : chunkMs
    let r: MediaRecorder
    try {
      r = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined)
    } catch {
      onError?.('recorder_init_failed')
      return
    }
    const chunks: Blob[] = []
    r.ondataavailable = (ev: BlobEvent) => {
      if (ev.data && ev.data.size > 0) chunks.push(ev.data)
    }
    r.onstop = () => {
      const usedMime = r.mimeType || mime || 'audio/webm'
      const blob = new Blob(chunks, { type: usedMime })
      // Restart immediately (before awaiting transcription) so the gap
      // between chunks is minimal; final chunk (running=false) tears down.
      if (running) startChunk()
      else teardownStream()
      if (blob.size === 0) return
      audioSeq += 1
      const seq = audioSeq
      const filename = `meeting.${extForMime(usedMime)}`
      onAudioChunk?.(blob, seq, filename)
      void (async () => {
        setInFlight(1)
        try {
          const text = await transcribe(blob, {
            language,
            filename: `meeting.${extForMime(usedMime)}`,
          })
          const trimmed = (text || '').trim()
          if (trimmed) onSegment(trimmed)
        } catch (e) {
          // Forward the STABLE code (set by the transcribe wrapper) so the
          // panel can map it; fall back to the message/sentinel otherwise.
          const code =
            (e as { code?: string } | null)?.code ||
            (e instanceof Error ? e.message : 'transcription_failed')
          onError?.(code)
        } finally {
          setInFlight(-1)
        }
      })()
    }
    rec = r
    try {
      r.start()
    } catch {
      onError?.('recorder_start_failed')
      return
    }
    timer = setTimeout(() => {
      try { if (r.state !== 'inactive') r.stop() } catch { /* ignore */ }
    }, thisChunkMs)
  }

  const start = async () => {
    if (!isRecorderSupported()) {
      onError?.('recorder_unsupported')
      return
    }
    if (running) return
    let s: MediaStream
    try {
      s = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (e) {
      const name = (e as { name?: string } | null)?.name || 'mic_error'
      onError?.(name === 'NotAllowedError' ? 'mic_permission_denied' : 'mic_error')
      return
    }
    stream = s
    mime = pickMimeType()
    running = true
    chunkIndex = 0
    audioSeq = 0
    setRecording(true)
    startChunk()
  }

  const stop = () => {
    running = false
    setRecording(false)
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    const r = rec
    if (r && r.state !== 'inactive') {
      try { r.stop() } catch { teardownStream() }
    } else {
      teardownStream()
    }
  }

  return {
    isRecording: () => recording,
    isTranscribing: () => inFlight > 0,
    start,
    stop,
  }
}
